import type {
	BinaryDateFilterRule,
	BinaryNumericFilterRule,
	BooleanFilterRule,
	NullableStringArrayFilterRule,
	ReferenceFilterRule,
	StringArrayFilterRule,
	StringFilterRule,
	TernaryDateFilterRule,
	TernaryNumericFilterRule,
} from '@/shared/generated'
import type { FilterRuleModel } from './types'
import type { FilterSchema } from './validation'

import { entries, map, pipe } from 'lodash/fp'

import { encodeRuleValue, normilizeValue } from '@/shared/tools/filtering'

import { validateFilter, validateFilters } from './validation'

/**
 * Transform query object to filter rules array
 * @example { a__eq: 1, b__eq: 2 } => [
 * 	{ name: 'a', operation: 'eq', value: 1 },
 * 	{ name: 'b', operation: 'eq', value: 2 }
 * ]
 * @param queryObject - object with query params to be converted to filter rules
 * @returns - array of filter rules
 */
export const toFilterRules: (queryObject: object) => FilterRuleModel[] = pipe([
	entries,
	map<[string, string | number], FilterRuleModel>(([item, value]) => {
		const [name, operation] = item.split('__')

		return {
			name,
			operation,
			value: normilizeValue(operation, value),
		} as FilterRuleModel
	}),
])

const isRefRule = (rule: FilterRuleModel): rule is ReferenceFilterRule =>
	'reference' in rule
const isBasicRule = (
	rule: FilterRuleModel
): rule is
	| BinaryNumericFilterRule
	| TernaryNumericFilterRule
	| BooleanFilterRule
	| StringFilterRule
	| NullableStringArrayFilterRule
	| StringArrayFilterRule
	| BinaryDateFilterRule
	| TernaryDateFilterRule => !isRefRule(rule)

/**
 * Transform filter rules to query object
 * @example
 * ```javascript
 * [
 * 	{ name: 'a', operation: 'eq', value: 1 },
 * 	{ name: 'b', operation: 'eq', value: 2 }
 * ] => {
 * 	a__eq: 1,
 * 	b__eq: 2
 * }
 * ```
 * @param rules - array of filter rules to be converted to query object
 * @returns - object with query params
 */
export const rulesToQueryObject = (rules: FilterSchema[]) =>
	Object.fromEntries(
		rules
			.filter(isBasicRule)
			.map((rule) => [
				`${rule.name}__${rule.operation}`,

				encodeRuleValue(rule.value as any),
			])
	)

export function isValidRule(rule: FilterRuleModel) {
	return validateFilter(rule).success
}
export function isValidFilterRules(rules: FilterRuleModel[]) {
	return validateFilters(rules).success
}
